import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Field } from '../components/ui.jsx';
import { useToast } from '../state/ToastContext.jsx';
import { useTitle } from '../lib/hooks.js';
import { ApiError, api } from '../api/client.js';

const ResetPassword = () => {
  useTitle('Reset your password');
  const { token } = useParams();
  const toast = useToast();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError('');

    if (password !== passwordConfirm) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setError('Use at least 8 characters.');
      return;
    }

    setBusy(true);

    try {
      await api.post('/auth/reset-password', { token, newPassword: password });
      setSuccess(true);
      toast.success('Password reset successfully!');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      if (err instanceof ApiError && err.details) {
        setError(err.details.token || err.message);
      } else {
        setError(err.message || 'Something went wrong. Try requesting a new reset link.');
      }
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <div className="auth-page container">
        <div className="auth-card">
          <h1>Password reset successfully</h1>
          <p className="muted">You can now sign in with your new password.</p>

          <div className="notice notice-good" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
            Redirecting to sign in...
          </div>

          <p className="auth-alt">
            <Link to="/login">Sign in now</Link>
          </p>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="auth-page container">
        <div className="auth-card">
          <h1>Invalid reset link</h1>
          <p className="muted">The reset link is missing or invalid.</p>

          <div className="notice notice-bad" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
            Request a new password reset link.
          </div>

          <p className="auth-alt">
            <Link to="/forgot-password">Request a new reset link</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page container">
      <div className="auth-card">
        <h1>Reset your password</h1>
        <p className="muted">
          Enter your new password. The reset link expires in 24 hours.
        </p>

        {error ? <div className="notice notice-bad">{error}</div> : null}

        <form className="stack" onSubmit={submit}>
          <Field label="New password" hint="At least 8 characters." required>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={busy}
            />
          </Field>

          <Field label="Confirm password" required>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={passwordConfirm}
              onChange={(event) => setPasswordConfirm(event.target.value)}
              disabled={busy}
            />
          </Field>

          <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={busy}>
            {busy ? 'Resetting...' : 'Reset password'}
          </button>
        </form>

        <p className="auth-alt">
          Remember your password? <Link to="/login">Sign in instead</Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
